import { createApiClient, fetchWhoAmI, type WhoAmI } from "@kontextwerk/web-common";

import { clearToken, getToken } from "./auth";
import type {
  CostDimensions,
  CostFilters,
  CostRecordsResponse,
  CostSummaryResponse,
  CostTimeseriesResponse,
  GroupBy,
  ProviderHealthResponse,
  VersionInfo,
} from "./types";

export { ApiError } from "@kontextwerk/web-common";

const call = createApiClient({
  baseUrl: "/core/v1",
  getToken,
  onUnauthorized: clearToken,
});

function appendFilters(params: URLSearchParams, f: CostFilters): void {
  if (f.pack_id) params.set("pack_id", f.pack_id);
  if (f.organization_id) params.set("organization_id", f.organization_id);
  if (f.realm_id) params.set("realm_id", f.realm_id);
  if (f.op_class) params.set("op_class", f.op_class);
  if (f.cost_layer) params.set("cost_layer", f.cost_layer);
  if (f.provider) params.set("provider", f.provider);
}

export async function fetchMe(): Promise<WhoAmI> {
  return fetchWhoAmI(call);
}

export async function fetchVersion(): Promise<VersionInfo> {
  return call<VersionInfo>("/version", {}, { auth: false });
}

export async function fetchCostSummary(
  fromDate: string,
  toDate: string,
  groupBy: GroupBy,
  filters: CostFilters = {},
  includeTodayLive?: boolean,
): Promise<CostSummaryResponse> {
  const params = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    group_by: groupBy,
  });
  appendFilters(params, filters);
  if (includeTodayLive !== undefined) {
    params.set("include_today_live", String(includeTodayLive));
  }
  return call<CostSummaryResponse>(`/cost/summary?${params}`);
}

export async function fetchCostTimeseries(
  fromDate: string,
  toDate: string,
  granularity: "day" | "hour" = "day",
  filters: CostFilters = {},
  includeTodayLive?: boolean,
): Promise<CostTimeseriesResponse> {
  const params = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    granularity,
  });
  appendFilters(params, filters);
  if (includeTodayLive !== undefined) {
    params.set("include_today_live", String(includeTodayLive));
  }
  return call<CostTimeseriesResponse>(`/cost/timeseries?${params}`);
}

export async function fetchCostRecords(
  fromDate: string,
  toDate: string,
  filters: CostFilters = {},
  cursor: number | null = null,
  limit = 50,
): Promise<CostRecordsResponse> {
  const params = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    limit: String(limit),
  });
  appendFilters(params, filters);
  if (cursor !== null) params.set("cursor", String(cursor));
  return call<CostRecordsResponse>(`/cost/records?${params}`);
}

export async function fetchCostDimensions(
  fromDate: string,
  toDate: string,
): Promise<CostDimensions> {
  const params = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
  });
  return call<CostDimensions>(`/cost/dimensions?${params}`);
}

export async function fetchProviderHealth(
  fromDate: string,
  toDate: string,
): Promise<ProviderHealthResponse> {
  const params = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
  });
  return call<ProviderHealthResponse>(`/admin/cost/pricing-health?${params}`);
}
